import React from "react";
import {
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
  withStyles,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import DeleteIcon from "@material-ui/icons/Delete";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import api from "../../../service/api";
import dataforms from "../../../service/dataforms";
import { COLUMN_TYPES, createColumn, getFieldDefinition } from "./fieldTypes";

const BPF_SERVER_URL = process.env.REACT_APP_BPF_SERVER_URL;

// Tipos de campo que guardam itens de produto (origem de `prefill_from`).
const PRODUCT_TYPES = ["product-order", "product-table"];

// Editor de lista de opções — serve para `select`/`select-auto` (só rótulo) e
// para `checkbox` (rótulo + a linha de observação, que é `type: "obs"`).
class OptionsEditor extends React.Component {
  change = (index, label) => {
    const options = this.props.options.map((option, i) =>
      i === index ? { ...option, label } : option
    );
    this.props.onChange(options);
  };

  remove = (index) => {
    this.props.onChange(this.props.options.filter((_, i) => i !== index));
  };

  move = (index, to) => {
    if (to < 0 || to >= this.props.options.length) return;
    const options = [...this.props.options];
    const [moved] = options.splice(index, 1);
    options.splice(to, 0, moved);
    this.props.onChange(options);
  };

  add = (type) => {
    const option =
      type === "obs"
        ? { label: "Observação", type: "obs" }
        : this.props.checkbox
        ? { label: "", type: "checkbox", checked: false }
        : { label: "" };
    this.props.onChange([...this.props.options, option]);
  };

  render() {
    const { classes, options, checkbox } = this.props;

    return (
      <div>
        {options.map((option, index) => (
          <div key={index} className={classes.optionRow}>
            <TextField
              fullWidth
              size="small"
              label={option.type === "obs" ? "Observação (texto livre)" : `Opção ${index + 1}`}
              value={option.label == null ? "" : option.label}
              onChange={(event) => this.change(index, event.target.value)}
            />
            <IconButton
              size="small"
              disabled={index === 0}
              onClick={() => this.move(index, index - 1)}
            >
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              disabled={index === options.length - 1}
              onClick={() => this.move(index, index + 1)}
            >
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => this.remove(index)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </div>
        ))}
        <Button size="small" startIcon={<AddIcon />} onClick={() => this.add("option")}>
          Opção
        </Button>
        {checkbox && (
          <Button size="small" startIcon={<AddIcon />} onClick={() => this.add("obs")}>
            Observação
          </Button>
        )}
      </div>
    );
  }
}

const StyledOptionsEditor = withStyles((theme) => ({
  optionRow: {
    display: "flex",
    alignItems: "flex-end",
    marginBottom: theme.spacing(0.5),
  },
}))(OptionsEditor);

/**
 * Inspetor: todas as propriedades do campo selecionado, editáveis.
 *
 * O construtor antigo só deixava renomear um campo já adicionado — qualquer
 * outro ajuste era apagar e refazer. Aqui cada tipo mostra as chaves que o DSL
 * define para ele (docs/02-esquema-json.md), incluindo as colunas de tabela.
 */
class Inspector extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      // Campos do formulário-alvo de um `form-link`, por índice.
      linkFields: [],
      linkLoadingFor: null,
      // Campos de produtos do form-alvo do vínculo escolhido em `prefill_from`.
      prefillSources: [],
      prefillLoadingFor: null,
      uploading: false,
    };
  }

  componentDidMount() {
    this.loadLinkFields();
    this.loadPrefillSources();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.field !== this.props.field) {
      this.loadLinkFields();
      this.loadPrefillSources();
    }
  }

  loadLinkFields = () => {
    const { field } = this.props;
    if (!field || field.type !== "form-link" || !field.target_form_id) return;
    if (this.state.linkLoadingFor === field.target_form_id) return;

    this.setState({ linkLoadingFor: field.target_form_id });
    dataforms
      .getFormLayout(field.target_form_id)
      .then((layout) => {
        this.setState({
          linkFields: (layout || []).map((f, index) => ({
            index,
            label: (f.component && f.component.label) || `Campo ${index + 1}`,
            type: f.type,
          })),
        });
      })
      .catch(() => this.setState({ linkFields: [] }));
  };

  /* ------------------------ preencher a partir de vínculo ------------------------ */

  // Form-link do layout atual para onde o `prefill_from` aponta.
  prefillLink = () => {
    const { field, fields } = this.props;
    if (!field || !field.prefill_from) return null;
    const link = (fields || [])[field.prefill_from.link_index];
    return link && link.type === "form-link" ? link : null;
  };

  loadPrefillSources = () => {
    const link = this.prefillLink();
    const targetFormId = link ? link.target_form_id : null;
    if (!targetFormId) {
      if (this.state.prefillLoadingFor !== null) {
        this.setState({ prefillSources: [], prefillLoadingFor: null });
      }
      return;
    }
    if (this.state.prefillLoadingFor === targetFormId) return;

    this.setState({ prefillSources: [], prefillLoadingFor: targetFormId });
    dataforms
      .getFormLayout(targetFormId)
      .then((layout) => {
        const sources = (layout || [])
          .map((f, index) => ({
            index,
            label: (f.component && f.component.label) || `Campo ${index + 1}`,
            type: f.type,
          }))
          .filter((f) => PRODUCT_TYPES.includes(f.type));
        this.setState({ prefillSources: sources });
        // Um só campo de produtos no alvo: já fica escolhido.
        const { field } = this.props;
        const current = field && field.prefill_from;
        if (current && current.source_index == null && sources.length === 1) {
          this.patch({ prefill_from: { ...current, source_index: sources[0].index } });
        }
      })
      .catch(() => this.setState({ prefillSources: [] }));
  };

  handlePrefillLink = (event) => {
    const value = event.target.value;
    // Trocar o vínculo invalida o campo de origem (é índice do outro layout).
    // Sem cópia, não há preço copiado para mostrar.
    if (value === "") {
      this.patch({ prefill_from: undefined, show_prices: undefined });
      return;
    }
    this.patch({ prefill_from: { link_index: value, source_index: null } });
  };

  handlePrefillSource = (event) => {
    this.patch({
      prefill_from: { ...this.props.field.prefill_from, source_index: event.target.value },
    });
  };

  toggleLinkFieldPrices = (index) => {
    const fields = (this.props.field.fields || []).map((f) =>
      f.index === index ? { ...f, show_prices: f.show_prices ? undefined : true } : f
    );
    this.patch({ fields });
  };

  patch = (patch) => this.props.onChange(patch);

  changeLabel = (label) => this.props.onChangeLabel(label);

  /* ------------------------------- form-link ------------------------------ */

  handleSelectLinkForm = (event) => {
    const formId = event.target.value;
    const form = (this.props.linkForms || []).find((f) => f.id === formId);
    this.setState({ linkFields: [], linkLoadingFor: null });
    // Trocar o alvo invalida os campos trazidos (são índices do outro layout).
    this.patch({
      target_form_id: formId,
      target_form_name: form ? form.name : "",
      fields: [],
    });
  };

  toggleLinkField = (index) => {
    const current = this.props.field.fields || [];
    const exists = current.some((f) => f.index === index);
    const meta = this.state.linkFields.find((f) => f.index === index);

    const fields = exists
      ? current.filter((f) => f.index !== index)
      : [
          ...current,
          {
            index,
            label: meta ? meta.label : `Campo ${index + 1}`,
            type: meta ? meta.type : null,
          },
        ].sort((a, b) => a.index - b.index);

    this.patch({ fields });
  };

  /* --------------------------------- imagem -------------------------------- */

  handleUploadImage = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    this.setState({ uploading: true });

    api
      .post("/storeImage", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((resp) => {
        this.setState({ uploading: false });
        this.patch({ source: resp.data.data.result });
      })
      .catch(() => this.setState({ uploading: false }));
  };

  /* ---------------------------- campos por item ---------------------------- */

  changeExtraField = (index, patch) => {
    const extra = (this.props.field.extra_fields || []).map((item, i) =>
      i === index ? { ...item, ...patch } : item
    );
    this.patch({ extra_fields: extra });
  };

  addExtraField = () => {
    const extra = [...(this.props.field.extra_fields || []), { label: "", type_input: "text" }];
    this.patch({ extra_fields: extra });
  };

  removeExtraField = (index) => {
    const extra = (this.props.field.extra_fields || []).filter((_, i) => i !== index);
    // Lista vazia: some com a chave em vez de gravar `[]` no layout.
    this.patch({ extra_fields: extra.length > 0 ? extra : undefined });
  };

  /* -------------------------------- colunas -------------------------------- */

  // product-table: copiar os itens de um registro vinculado (ex.: Carregamento
  // herda os itens do Pedido). Ver libs/linkPrefill.js.
  renderPrefillFrom = () => {
    const { classes, field, fields } = this.props;
    const links = (fields || [])
      .map((f, index) => ({ f, index }))
      .filter(({ f }) => f.type === "form-link");
    const prefill = field.prefill_from;
    const link = this.prefillLink();

    return (
      <div className={classes.field}>
        <Typography variant="caption" color="textSecondary">
          Preencher a partir de vínculo
        </Typography>
        {links.length === 0 ? (
          <Typography variant="caption" display="block">
            Adicione um campo "Vínculo de Formulário" neste layout para copiar os itens
            de outro registro.
          </Typography>
        ) : (
          <FormControl fullWidth>
            <InputLabel>Vínculo de origem</InputLabel>
            <Select
              value={prefill ? prefill.link_index : ""}
              onChange={this.handlePrefillLink}
            >
              <MenuItem value="">
                <em>Não copiar (preenchido à mão)</em>
              </MenuItem>
              {links.map(({ f, index }) => (
                <MenuItem key={index} value={index}>
                  {`${index + 1}. ${(f.component && f.component.label) || "Vínculo"}`}
                  {f.target_form_name ? ` (${f.target_form_name})` : ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {prefill && link && !link.target_form_id && (
          <Typography variant="caption" display="block" color="error">
            O vínculo escolhido ainda não tem formulário-alvo.
          </Typography>
        )}

        {prefill && link && link.target_form_id && (
          <FormControl fullWidth className={classes.prefillSource}>
            <InputLabel>Campo de produtos de origem</InputLabel>
            <Select
              value={prefill.source_index == null ? "" : prefill.source_index}
              onChange={this.handlePrefillSource}
            >
              {this.state.prefillSources.length === 0 && (
                <MenuItem value="" disabled>
                  <em>Nenhum campo de produtos em "{link.target_form_name}"</em>
                </MenuItem>
              )}
              {this.state.prefillSources.map((source) => (
                <MenuItem key={source.index} value={source.index}>
                  {`${source.index + 1}. ${source.label}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {prefill && (
          <FormControlLabel
            control={
              <Switch
                checked={field.show_prices === true}
                onChange={(event) =>
                  this.patch({ show_prices: event.target.checked ? true : undefined })
                }
              />
            }
            label="Mostrar preços copiados"
          />
        )}
      </div>
    );
  };

  renderColumn = (column, columnIndex) => {
    const { classes, field } = this.props;
    const columns = field.columns || [];

    return (
      <Paper key={columnIndex} variant="outlined" className={classes.column}>
        <div className={classes.columnHeader}>
          <Typography variant="caption" className={classes.columnIndex}>
            Coluna {columnIndex + 1}
          </Typography>
          <div>
            <IconButton
              size="small"
              disabled={columnIndex === 0}
              onClick={() => this.props.onColumnMove(columnIndex, columnIndex - 1)}
            >
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              disabled={columnIndex === columns.length - 1}
              onClick={() => this.props.onColumnMove(columnIndex, columnIndex + 1)}
            >
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => this.props.onColumnDuplicate(columnIndex)}
            >
              <FileCopyIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => this.props.onColumnRemove(columnIndex)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </div>
        </div>

        <TextField
          fullWidth
          size="small"
          label="Título da coluna"
          value={(column.component && column.component.label) || ""}
          onChange={(event) =>
            this.props.onColumnChange(columnIndex, {
              component: { ...(column.component || {}), label: event.target.value },
            })
          }
        />

        <FormControl fullWidth className={classes.field}>
          <InputLabel>Tipo</InputLabel>
          <Select
            value={column.type}
            onChange={(event) => {
              // Trocar o tipo recria a coluna: as chaves do tipo antigo
              // (options, index_signature) não valem para o novo.
              const next = createColumn(event.target.value);
              next.component.label = (column.component && column.component.label) || "";
              if (column.identifier) next.identifier = true;
              if (column.required) next.required = true;
              this.props.onColumnReplace(columnIndex, next);
            }}
          >
            {COLUMN_TYPES.map((definition) => (
              <MenuItem key={definition.type} value={definition.type}>
                {definition.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={column.identifier === true}
              onChange={(event) =>
                this.props.onColumnChange(columnIndex, {
                  identifier: event.target.checked ? true : undefined,
                })
              }
            />
          }
          label="Identificador"
        />

        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={column.required === true}
              onChange={(event) =>
                this.props.onColumnChange(columnIndex, {
                  required: event.target.checked ? true : undefined,
                })
              }
            />
          }
          label="Obrigatório"
        />

        {column.type === "input" && (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={column.sum === true}
                onChange={(event) =>
                  this.props.onColumnChange(columnIndex, {
                    sum: event.target.checked ? true : undefined,
                  })
                }
              />
            }
            label="Somar valores"
          />
        )}

        {column.type === "checkbox" && (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={column.multi === true}
                  onChange={(event) =>
                    this.props.onColumnChange(columnIndex, {
                      multi: event.target.checked ? true : undefined,
                    })
                  }
                />
              }
              label="Seleção múltipla"
            />
            <StyledOptionsEditor
              checkbox
              options={column.options || []}
              onChange={(options) => this.props.onColumnChange(columnIndex, { options })}
            />
          </>
        )}

        {column.type === "select" && (
          <StyledOptionsEditor
            options={column.options || []}
            onChange={(options) => this.props.onColumnChange(columnIndex, { options })}
          />
        )}
      </Paper>
    );
  };

  /* --------------------------------- render -------------------------------- */

  render() {
    const { classes, field, index } = this.props;

    if (!field) {
      return (
        <div className={classes.empty}>
          <Typography variant="body2" color="textSecondary">
            Selecione um campo na lista para editar as propriedades dele.
          </Typography>
        </div>
      );
    }

    const definition = getFieldDefinition(field.type);
    const can = definition.supports;

    return (
      <div className={classes.container}>
        <Typography variant="subtitle2" className={classes.title}>
          {definition.label}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Campo {index + 1} · {definition.description}
        </Typography>

        <Divider className={classes.divider} />

        {can.label && (
          <TextField
            fullWidth
            id="inspector-label"
            label="Rótulo"
            value={(field.component && field.component.label) || ""}
            onChange={(event) => this.changeLabel(event.target.value)}
            className={classes.field}
          />
        )}

        {can.text && (
          <TextField
            fullWidth
            multiline
            rows={6}
            id="inspector-text"
            label="Texto"
            value={(field.component && field.component.label) || ""}
            onChange={(event) => this.changeLabel(event.target.value)}
            className={classes.field}
          />
        )}

        {can.identifier && (
          <div className={classes.field}>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={field.identifier === true}
                  onChange={(event) =>
                    this.patch({ identifier: event.target.checked ? true : undefined })
                  }
                />
              }
              label="Usar como identificador"
            />
            <Typography variant="caption" color="textSecondary" display="block">
              O valor deste campo vira o identificador do preenchimento (o que
              aparece na listagem e na busca).
              {this.props.identifierIndex >= 0 &&
                this.props.identifierIndex !== index &&
                ` Hoje quem define isso é o campo ${this.props.identifierIndex + 1} — com mais de um marcado, vence o último.`}
            </Typography>
          </div>
        )}

        {can.required && (
          <div className={classes.field}>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={field.required === true}
                  onChange={(event) =>
                    this.patch({ required: event.target.checked ? true : undefined })
                  }
                />
              }
              label="Obrigatório"
            />
            <Typography variant="caption" color="textSecondary" display="block">
              O preenchimento não pode ser salvo sem este campo.
            </Typography>
          </div>
        )}

        {can.hidePdf && (
          <FormControlLabel
            className={classes.field}
            control={
              <Switch
                color="primary"
                checked={field.hide_pdf === true}
                onChange={(event) =>
                  this.patch({ hide_pdf: event.target.checked ? true : undefined })
                }
              />
            }
            label="Não exibir no PDF"
          />
        )}

        {can.checkbox && (
          <div className={classes.field}>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={field.multi === true}
                  onChange={(event) =>
                    this.patch({ multi: event.target.checked ? true : undefined })
                  }
                />
              }
              label="Seleção múltipla"
            />
            <Typography variant="caption" color="textSecondary" display="block">
              Desligado, marcar uma opção desmarca as outras.
            </Typography>
            <StyledOptionsEditor
              checkbox
              options={field.options || []}
              onChange={(options) => this.patch({ options })}
            />
          </div>
        )}

        {can.options && (
          <div className={classes.field}>
            <Typography variant="caption" color="textSecondary">
              Opções
            </Typography>
            <StyledOptionsEditor
              options={field.options || []}
              onChange={(options) => this.patch({ options })}
            />
          </div>
        )}

        {can.urlApi && (
          <TextField
            fullWidth
            id="inspector-url-api"
            label="URL da API"
            placeholder="https://exemplo.com/itens"
            helperText="A resposta precisa ser uma lista de { id, nome }."
            value={field.url_api || ""}
            onChange={(event) => this.patch({ url_api: event.target.value })}
            className={classes.field}
          />
        )}

        {can.mediaUrl && (
          <TextField
            fullWidth
            id="inspector-media-url"
            label="URL do arquivo"
            value={field.source || ""}
            onChange={(event) => this.patch({ source: event.target.value })}
            className={classes.field}
          />
        )}

        {can.signature && (
          <Typography variant="caption" color="textSecondary" className={classes.field}>
            A posição da assinatura (`index_signature`) é recalculada sozinha
            quando os campos são reordenados.
          </Typography>
        )}

        {can.image && (
          <div className={classes.field}>
            <Button variant="outlined" component="label" size="small" disabled={this.state.uploading}>
              {this.state.uploading ? "Enviando..." : field.source ? "Trocar imagem" : "Enviar imagem"}
              <input hidden type="file" accept="image/*" onChange={this.handleUploadImage} />
            </Button>
            {field.source && (
              <div className={classes.imagePreview}>
                <img
                  alt={field.source}
                  src={`${BPF_SERVER_URL}/showImage?filename=${field.source}`}
                  className={classes.image}
                />
              </div>
            )}
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={field.centered === true}
                  onChange={(event) =>
                    this.patch({ centered: event.target.checked ? true : undefined })
                  }
                />
              }
              label="Centralizada"
            />
            <FormControl fullWidth>
              <InputLabel>Escala</InputLabel>
              <Select
                value={field.scale || 100}
                onChange={(event) => this.patch({ scale: event.target.value })}
              >
                {[25, 50, 75, 100].map((scale) => (
                  <MenuItem key={scale} value={scale}>
                    {scale}%
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        )}

        {can.extraFields && (
          <div className={classes.field}>
            <Typography variant="caption" color="textSecondary">
              Campos adicionais por item (ex.: Lote, Validade)
            </Typography>
            {(field.extra_fields || []).map((extra, extraIndex) => (
              <div key={extraIndex} className={classes.optionRow}>
                <TextField
                  size="small"
                  label="Rótulo"
                  value={extra.label || ""}
                  onChange={(event) =>
                    this.changeExtraField(extraIndex, { label: event.target.value })
                  }
                />
                <FormControl size="small" className={classes.extraType}>
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={extra.type_input || "text"}
                    onChange={(event) =>
                      this.changeExtraField(extraIndex, { type_input: event.target.value })
                    }
                  >
                    <MenuItem value="text">Texto</MenuItem>
                    <MenuItem value="date">Data</MenuItem>
                  </Select>
                </FormControl>
                <IconButton size="small" onClick={() => this.removeExtraField(extraIndex)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </div>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={this.addExtraField}>
              Campo por item
            </Button>
          </div>
        )}

        {can.prefillFrom && this.renderPrefillFrom()}

        {can.formLink && (
          <div className={classes.field}>
            <FormControl fullWidth>
              <InputLabel>Formulário a vincular</InputLabel>
              <Select
                value={field.target_form_id || ""}
                onChange={this.handleSelectLinkForm}
              >
                <MenuItem value="">
                  <em>Selecione</em>
                </MenuItem>
                {(this.props.linkForms || []).map((form) => (
                  <MenuItem key={form.id} value={form.id}>
                    {form.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {field.target_form_id && (
              <div className={classes.linkFields}>
                <Typography variant="caption" color="textSecondary">
                  Campos trazidos do registro vinculado
                </Typography>
                {this.state.linkFields.length === 0 && (
                  <Typography variant="caption" display="block">
                    Carregando os campos do formulário-alvo...
                  </Typography>
                )}
                {this.state.linkFields.map((target) => {
                  const brought = (field.fields || []).find((f) => f.index === target.index);
                  return (
                    <div key={target.index}>
                      <FormControlLabel
                        className={classes.linkField}
                        control={
                          <Checkbox
                            size="small"
                            checked={!!brought}
                            onChange={() => this.toggleLinkField(target.index)}
                          />
                        }
                        label={
                          <span>
                            {target.label}{" "}
                            <Chip size="small" label={target.type} className={classes.linkType} />
                          </span>
                        }
                      />
                      {brought && target.type === "product-order" && (
                        <FormControlLabel
                          className={classes.linkFieldOption}
                          control={
                            <Checkbox
                              size="small"
                              checked={!!brought.show_prices}
                              onChange={() => this.toggleLinkFieldPrices(target.index)}
                            />
                          }
                          label="Mostrar valores"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {can.columns && (
          <div className={classes.field}>
            <div className={classes.columnsHeader}>
              <Typography variant="caption" color="textSecondary">
                Colunas da tabela
              </Typography>
              <Tooltip title="Adicionar coluna de texto">
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => this.props.onColumnAdd("input")}
                >
                  Coluna
                </Button>
              </Tooltip>
            </div>
            {(field.columns || []).length === 0 && (
              <Typography variant="caption" color="textSecondary">
                Sem colunas: a tabela ainda não coleta nada.
              </Typography>
            )}
            {(field.columns || []).map(this.renderColumn)}
          </div>
        )}
      </div>
    );
  }
}

const styles = (theme) => ({
  container: {
    padding: theme.spacing(2),
  },
  empty: {
    padding: theme.spacing(6, 2),
    textAlign: "center",
  },
  title: {
    fontWeight: 700,
  },
  divider: {
    margin: theme.spacing(1.5, 0),
  },
  field: {
    marginTop: theme.spacing(2),
    display: "block",
  },
  optionRow: {
    display: "flex",
    alignItems: "flex-end",
    marginBottom: theme.spacing(0.5),
  },
  extraType: {
    minWidth: 90,
    marginLeft: theme.spacing(1),
  },
  column: {
    padding: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  columnHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  columnIndex: {
    fontWeight: 700,
  },
  columnsHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  linkFields: {
    marginTop: theme.spacing(1),
  },
  linkField: {
    display: "flex",
  },
  linkFieldOption: {
    marginLeft: theme.spacing(4),
    display: "block",
  },
  prefillSource: {
    marginTop: theme.spacing(1),
  },
  linkType: {
    height: 18,
    fontSize: 10,
  },
  imagePreview: {
    marginTop: theme.spacing(1),
  },
  image: {
    maxWidth: "100%",
    maxHeight: 160,
  },
});

export default withStyles(styles)(Inspector);
